/**
 * ReopenActiveBanner — Phase 8 · Period Reopen Workflow (Feb 2026).
 *
 * Global red banner that appears across ALL governed pages while a
 * CLOSED accounting period is temporarily reopened. Drives operator
 * awareness — "you are now posting into a controlled window".
 *
 * Behavior:
 *   • Polls /api/period-reopen/active/window every 30s.
 *   • Renders nothing when no active window.
 *   • Shows period name, approver name, remaining time, and a
 *     direct link to the Reopen Center.
 *   • Lives at the top of the main layout so it overlays every page.
 *
 * READ-ONLY — this component never mutates state. The Reopen Center
 * is the only surface that issues close-now / approve / reject.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { axiosInstance } from '../App';
import { ShieldAlert, ExternalLink } from 'lucide-react';

const POLL_MS = 30_000;

export default function ReopenActiveBanner() {
  const [win, setWin] = useState(null);
  const [now, setNow] = useState(Date.now());
  const timerRef = useRef(null);
  const pollRef  = useRef(null);

  useEffect(() => {
    let alive = true;
    const fetchWin = async () => {
      try {
        const r = await axiosInstance.get('/period-reopen/active/window');
        if (alive) setWin(r.data?.active ? r.data.window : null);
      } catch (_) { /* silent — banner is best-effort */ }
    };
    fetchWin();
    pollRef.current = setInterval(fetchWin, POLL_MS);
    timerRef.current = setInterval(() => setNow(Date.now()), 1_000);
    return () => {
      alive = false;
      clearInterval(pollRef.current);
      clearInterval(timerRef.current);
    };
  }, []);

  const remainingMs = useMemo(() => {
    if (!win?.expires_at) return 0;
    return Math.max(0, new Date(win.expires_at).getTime() - now);
  }, [win, now]);

  if (!win || remainingMs <= 0) return null;

  const mins = Math.floor(remainingMs / 60_000);
  const secs = Math.floor((remainingMs % 60_000) / 1000).toString().padStart(2, '0');

  return (
    <div
      data-testid="reopen-banner"
      className="bg-gradient-to-r from-rose-600 to-red-700 text-white px-4 py-2.5 shadow-md
                 flex items-center justify-between gap-4 sticky top-0 z-50 border-b border-red-900"
    >
      <div className="flex items-center gap-3 min-w-0">
        <ShieldAlert size={18} className="shrink-0 animate-pulse" />
        <div className="text-sm min-w-0">
          <span className="font-bold">Period Reopened</span>
          <span className="opacity-95 mx-2">·</span>
          <span className="font-mono">{win.period_name}</span>
          <span className="opacity-90 mx-2">·</span>
          <span className="opacity-90">approved by {win.approved_by_name || '—'}</span>
          <span className="opacity-90 mx-2">·</span>
          <span className="font-mono">{mins}m {secs}s</span>
          <span className="opacity-90 ml-2 hidden md:inline">
            · All postings monitored
          </span>
        </div>
      </div>
      <a
        href="/period-reopen"
        data-testid="reopen-banner-link"
        className="inline-flex items-center gap-1 bg-white/20 hover:bg-white/30
                   text-white text-xs font-semibold px-3 py-1 rounded-md shrink-0"
      >
        View Window <ExternalLink size={12} />
      </a>
    </div>
  );
}
